import { ReactNode, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { IconButton } from './IconButton';
import { IconX } from './Icon';
import { useFocusTrap, tabbables } from './useFocusTrap';
import { dialog } from '@/shared/store/dialogStore';

type Props = {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  width?: string;
  // The dialog holds input that is not saved yet: Escape, a backdrop click and
  // the Close button ask "Discard changes?" first. A footer Cancel button is
  // the caller's own and closes directly.
  dirty?: boolean;
  // Stacking class of the overlay. DialogHost passes one above the Help Center.
  z?: string;
};

// Open modals, oldest first. Only the newest one answers Escape and traps
// focus, so a confirm opened over a form dialog behaves on its own.
const openModals: symbol[] = [];

export function Modal({ open, onClose, title, children, footer, width = 'max-w-lg', dirty = false, z = 'z-50' }: Props) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  // Latest onClose / dirty without re-subscribing: callers pass a fresh arrow
  // on every render, and re-subscribing would reorder the open-modal stack.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  const idRef = useRef<symbol>(Symbol('modal'));
  const titleId = useId();
  const confirmingRef = useRef(false);

  // Escape, the backdrop and Close come through here.
  const requestCloseRef = useRef<() => void>(() => {});
  requestCloseRef.current = () => {
    if (!dirtyRef.current) { onCloseRef.current(); return; }
    if (confirmingRef.current) return;
    confirmingRef.current = true;
    void dialog.confirm({
      title: 'Discard changes?',
      message: 'What you entered in this dialog has not been saved.',
      confirmLabel: 'Discard',
      cancelLabel: 'Keep editing',
      danger: true,
    }).then((ok) => {
      confirmingRef.current = false;
      if (ok) { onCloseRef.current(); return; }
      // The click that asked had blurred focus to <body>; once the confirm has
      // handed focus back, bring it into this dialog if it is not already.
      setTimeout(() => {
        const panel = panelRef.current;
        if (!panel || panel.contains(document.activeElement)) return;
        (tabbables(panel).find((el) => !el.matches('[data-modal-close]')) ?? panel).focus();
      }, 0);
    });
  };

  // Escape closes the newest modal. On `document` (bubble phase), so it runs
  // before any window listener; preventDefault marks the key used, and
  // SubNav's "Escape pops the breadcrumb" (a window listener) skips a used
  // Escape, so closing a modal never also navigates away from the record.
  // No stopPropagation: listeners further out still see the key, marked.
  // An Escape a child already used (a calendar, a menu, a picker) is left alone.
  useEffect(() => {
    if (!open) return;
    const id = idRef.current;
    openModals.push(id);
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (openModals[openModals.length - 1] !== id) return;
      e.preventDefault();
      requestCloseRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      const i = openModals.indexOf(id);
      if (i >= 0) openModals.splice(i, 1);
    };
  }, [open]);

  // Focus in on open, Tab wraps inside, focus back to the opener on close.
  useFocusTrap(panelRef, {
    active: open,
    isTop: () => openModals[openModals.length - 1] === idRef.current,
    skip: '[data-modal-close]',
  });

  // Lock background scroll while the modal is open. Without this, tall
  // modals that themselves scroll still let the underlying page scroll,
  // which is visually janky.
  useEffect(() => {
    if (!open) return;
    const prev = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    return () => { document.documentElement.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  // Portaled to <html> so the backdrop and centring never depend on a
  // transformed or clipped ancestor.
  const tree = (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? titleId : undefined}
      className={`fixed inset-0 ${z} flex items-center justify-center p-4`}
      style={{ background: 'rgba(15, 23, 42, 0.5)' }}
      onClick={() => requestCloseRef.current()}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        className={`w-full ${width} bg-surface rounded-card shadow-2xl border border-border flex flex-col outline-none animate-dialog`}
        style={{ maxHeight: '90vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {title && (
          <div className="px-5 py-3 border-b border-border flex items-center justify-between shrink-0">
            <div id={titleId} className="font-semibold text-slate-900">{title}</div>
            <IconButton label="Close" data-modal-close onClick={() => requestCloseRef.current()}><IconX size={14} /></IconButton>
          </div>
        )}
        <div className="px-5 py-4 overflow-y-auto">{children}</div>
        {footer && <div className="px-5 py-3 border-t border-border flex justify-end gap-2 shrink-0">{footer}</div>}
      </div>
    </div>
  );

  return createPortal(tree, document.documentElement);
}
